import React, { Component } from 'react';
import PropTypes from 'prop-types';

import striptags from 'striptags';
import cx from 'classnames';
import deepEqual from 'deep-equal';
import displayName from 'component-lib/displayName';

import styles from './style.scss';

export default class SoftLimitTextArea extends Component {
    static displayName = displayName('Forms/SoftLimitTextArea');

    static propTypes = {
        disabled: PropTypes.bool,
        name: PropTypes.string,
        value: PropTypes.string,
        onChange: PropTypes.func,
        limit: PropTypes.number,
        minHeight: PropTypes.number,
        error: PropTypes.any,
    };

    static defaultProps = {
        disabled: false,
        name: '',
        value: '',
        onChange: () => {},
        limit: null,
        minHeight: null,
        error: null,
    };

    constructor(props) {
        super(props);

        // to perserve cursor location during standard editing,
        // we need to treat the contentEditable div as "unmanaged"
        // and ignore external value prop updates unless they're
        // different from what we expect them to be internally.
        this.internalValue = this.props.value;
    }

    componentDidMount() {
        this.div.innerText = this.internalValue;

        this.showSoftLimit();
    }

    shouldComponentUpdate(nextProps) {
        return nextProps.value !== this.internalValue || !deepEqual(nextProps.error, this.props.error);
    }

    componentWillUpdate(newProps) {
        if (newProps.value !== this.internalValue && this.div) {
            this.div.innerHTML = newProps.value;
            this.updateInternalValueFromDiv();
            this.showSoftLimit();
        }
    }

    onKeyDown = () => {
        this.handleUpdate();
    };

    onPaste = () => {
    // remove all html on a paste operation!
        this.stripHTML = true;

        setTimeout(this.handleUpdate, 20);
    };

    onFocus = () => {
        this.div.className += ` ${styles.focus}`;
    };

    onBlur = () => {
        this.div.className = this.div.className.replace(` ${styles.focus}`, '');
    };

    handleUpdate = () => {
        this.updateInternalValueFromDiv();
        this.props.onChange(this.internalValue);
        this.showSoftLimit();
    };

    updateInternalValueFromDiv() {
        if (this.stripHTML) {
            this.stripHTML = false;

            this.div.innerHTML = striptags(this.div.innerHTML, ['br']);
        }

        // <br> is added by contenteditable when you hit enter
        const lines = this.div.innerHTML.split('<br>');

        this.internalValue = lines.join('\n').replace(/&nbsp;/g, ' ');
    }

    showSoftLimit = () => {
        if (!this.backingDiv) {
            return;
        }

        const allLines = this.internalValue.split('\n');

        const result = allLines.reduce(
            (all, line) => {
                const { lines, remaining } = all;

                if (line.length > remaining) {
                    const ok = line.substr(0, remaining);
                    const invalid = line.substr(remaining);

                    let output = '';
                    if (ok.length > 0) {
                        output += `<span>${ok.replace(/ $/g, '&nbsp;')}</span>`;
                    }

                    output += `<span class="${styles.invalid}">${invalid.replace(/ $/g, '&nbsp;')}</span>`;

                    return {
                        lines: lines.concat([output]),
                        remaining: 0,
                    };
                }

                // a line break character takes up one space so we need to adjust
                // the remaining characters by that amount
                return {
                    lines: lines.concat([line.replace(/ $/g, '&nbsp;')]),
                    remaining: remaining - line.length - 1,
                };
            },
            { lines: [], remaining: this.props.limit },
        );

        this.backingDiv.innerHTML = result.lines.join('<br>');
    };

    render() {
        const classNameMaybeWithError = cx(styles.container, this.props.error && styles.error);

        const topProps = {
            className: classNameMaybeWithError,
        };

        if (this.props.error) {
            topProps['data-error'] = this.props.name;
        }

        const extraStyles = {};
        if (this.props.minHeight) {
            extraStyles.minHeight = this.props.minHeight;
        }

        const textAreaClassName = cx(styles.textarea, {
            [styles.disabled]: this.props.disabled,
        });

        return (
            <div {...topProps}>
                <div
                    className={styles['backing-textarea']}
                    style={extraStyles}
                    ref={(div) => {
                        this.backingDiv = div;
                    }}
                />
                <div
                    role="presentation"
                    className={textAreaClassName}
                    style={extraStyles}
                    data-input={this.props.name}
                    contentEditable={!this.props.disabled}
                    suppressContentEditableWarning
                    onKeyUp={this.onKeyDown}
                    onPaste={this.onPaste}
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    ref={(div) => {
                        this.div = div;
                    }}
                />
            </div>
        );
    }
}
